import type {
  BlockSwitchIssue,
  SwitchValidationRow
} from '@/components/building-block-form/utils/switchValidation'
import { ISSUE_SEVERITIES } from '@/constants/issueSeverity'

export type SwitchIssueDetail = {
  summary: string
  fieldDiffs: string[]
}

export type MaterialNameById = Record<number, string>

export const buildMaterialNamesById = (materials: Array<{ id: number; name: string }>) => {
  const map: MaterialNameById = {}
  for (const material of materials) {
    map[material.id] = material.name
  }
  return map
}

export const resolveIssueClass = (issue?: BlockSwitchIssue): string => {
  if (!issue) return ''
  return issue.severity === ISSUE_SEVERITIES.MISSING
    ? 'building-block-card-missing'
    : 'building-block-card-changed'
}

export const resolveIssueBadgeColor = (issue?: BlockSwitchIssue): string => {
  if (!issue) return 'default'
  return issue.severity === ISSUE_SEVERITIES.MISSING ? 'error' : 'warning'
}

export const resolveIssueTitle = (issue?: BlockSwitchIssue): string => {
  if (!issue) return ''
  if (issue.severity === ISSUE_SEVERITIES.MISSING) return 'Missing values in target database'

  const baseName = issue.baseDatabaseName
  return baseName ? `Changed values from ${baseName}` : 'Changed values in target database'
}

const formatIssueRow = (row: SwitchValidationRow, materialNamesById: MaterialNameById): string => {
  const materialName = materialNamesById[row.material_id] || row.product_name
  return `Affected material ${materialName}`
}

const formatMissingMaterialMessage = (
  row: SwitchValidationRow,
  materialNamesById: MaterialNameById
): string => {
  const materialName = materialNamesById[row.material_id] || row.product_name
  return `Material ${materialName} is missing. Please remove this material or this custom building block in order to run calculation or add a new custom building block.`
}

const formatChangedFieldDiffs = (row: SwitchValidationRow): string[] => {
  const changedFields = row.changed_fields ?? {}
  const entries = Object.entries(changedFields)
  if (!entries.length) return ['Values changed']

  const normalizeNumber = (value: number): string => {
    return value.toFixed(2).replace(/\.?0+$/, '')
  }

  const toNumber = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value !== 'string') return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  const shouldFormatAsEuro = (fieldName: string): boolean => {
    return fieldName.toLowerCase() !== 'lifetime'
  }

  const formatValue = (value: unknown, fieldName: string): string => {
    const numericValue = toNumber(value)
    if (numericValue === null) return String(value ?? '-')
    const normalized = normalizeNumber(numericValue)
    if (!shouldFormatAsEuro(fieldName)) return normalized
    return `€ ${normalized}`
  }

  return entries.map(([fieldName, values]) => {
    const baseValue = formatValue(values?.base, fieldName)
    const targetValue = formatValue(values?.target, fieldName)
    return `${fieldName}: ${baseValue} -> ${targetValue}`
  })
}

export const buildMissingDetails = (
  issue: BlockSwitchIssue | undefined,
  materialNamesById: MaterialNameById
): SwitchIssueDetail[] => {
  if (!issue) return []
  return issue.missingRows.map((row) => ({
    summary: formatMissingMaterialMessage(row, materialNamesById),
    fieldDiffs: []
  }))
}

export const buildChangedDetails = (
  issue: BlockSwitchIssue | undefined,
  materialNamesById: MaterialNameById
): SwitchIssueDetail[] => {
  if (!issue) return []
  return issue.changedRows.map((row) => ({
    summary: formatIssueRow(row, materialNamesById),
    fieldDiffs: formatChangedFieldDiffs(row)
  }))
}
